import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Badge } from '../../../components/ui/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog';
import { DynamicFieldEditor } from './DynamicFieldEditor';
import type { DocumentTemplateType, DocumentTemplateField } from '../../../core/models/Document';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';

interface TemplateBuilderProps {
  template: Partial<DocumentTemplateType>;
  onChange: (template: Partial<DocumentTemplateType>) => void;
}

export const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  template,
  onChange,
}) => {
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [isFieldDialogOpen, setIsFieldDialogOpen] = useState(false);

  const handleAddField = () => {
    setEditingFieldIndex(null);
    setIsFieldDialogOpen(true);
  };

  const handleEditField = (index: number) => {
    setEditingFieldIndex(index);
    setIsFieldDialogOpen(true);
  };

  const handleSaveField = (field: DocumentTemplateField) => {
    const fields = template.dynamicFields || [];
    if (editingFieldIndex !== null) {
      // Update existing field
      const updatedFields = [...fields];
      updatedFields[editingFieldIndex] = field;
      onChange({ ...template, dynamicFields: updatedFields });
    } else {
      // Add new field
      onChange({ ...template, dynamicFields: [...fields, field] });
    }
    setIsFieldDialogOpen(false);
    setEditingFieldIndex(null);
  };

  const handleDeleteField = (index: number) => {
    const fields = template.dynamicFields || [];
    const updatedFields = fields.filter((_, i) => i !== index);
    onChange({ ...template, dynamicFields: updatedFields });
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const fields = template.dynamicFields || [];
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === fields.length - 1)
    ) {
      return;
    }

    const updatedFields = [...fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updatedFields[index], updatedFields[newIndex]] = [
      updatedFields[newIndex],
      updatedFields[index],
    ];
    onChange({ ...template, dynamicFields: updatedFields });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Template Name"
          value={template.name || ''}
          onChange={(e) => onChange({ ...template, name: e.target.value })}
          placeholder="e.g., Service Agreement"
          required
        />

        <div className="flex items-center space-x-2 pt-6">
          <Checkbox
            checked={template.isActive ?? true}
            onCheckedChange={(checked) =>
              onChange({ ...template, isActive: checked === true })
            }
          />
          <label className="text-sm font-medium">Active Template</label>
        </div>
      </div>

      <Textarea
        label="Description"
        value={template.description || ''}
        onChange={(e) => onChange({ ...template, description: e.target.value })}
        placeholder="Describe what this template is used for"
        rows={3}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">
            HTML Content <span className="text-error">*</span>
          </label>
          <span className="text-xs text-gray-500">
            Use placeholders like {'{{fieldName}}'}, {'{{title}}'}, {'{{client_name}}'}, {'{{client_email}}'}
          </span>
        </div>
        <Textarea
          value={template.content || ''}
          onChange={(e) => onChange({ ...template, content: e.target.value })}
          placeholder="<!DOCTYPE html>
<html>
<head>
  <title>{{title}}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; }
  </style>
</head>
<body>
  <h1>{{title}}</h1>
  <p>Client: {{client_name}}</p>
  <p>Email: {{client_email}}</p>
  <!-- Add your dynamic fields here: {{fieldName}} -->
</body>
</html>"
          rows={12}
          className="font-mono text-sm"
          required
        />
        <p className="text-xs text-gray-500">
          This HTML will be used as the template. Dynamic field placeholders will be replaced when creating documents.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Dynamic Fields</h3>
          <Button type="button" onClick={handleAddField} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Field
          </Button>
        </div>

        {template.dynamicFields && template.dynamicFields.length > 0 ? (
          <div className="space-y-2">
            {template.dynamicFields.map((field, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 flex items-start justify-between hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                    <span className="font-medium">{field.label}</span>
                    <Badge variant="default">{field.type}</Badge>
                    {field.required && (
                      <Badge variant="error">Required</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    Key: <code className="bg-gray-100 px-1 rounded">{field.key}</code>
                  </p>
                  {field.helpText && (
                    <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveField(index, 'up')}
                    >
                      ↑
                    </Button>
                  )}
                  {index < (template.dynamicFields?.length || 0) - 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveField(index, 'down')}
                    >
                      ↓
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditField(index)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteField(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <p className="text-gray-500 mb-4">No fields added yet</p>
            <Button type="button" onClick={handleAddField} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Add First Field
            </Button>
          </div>
        )}
      </div>

      <Dialog open={isFieldDialogOpen} onOpenChange={setIsFieldDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingFieldIndex !== null ? 'Edit Field' : 'Add Field'}
            </DialogTitle>
          </DialogHeader>
          <DynamicFieldEditor
            field={
              editingFieldIndex !== null
                ? template.dynamicFields?.[editingFieldIndex]
                : undefined
            }
            onSave={handleSaveField}
            onCancel={() => {
              setIsFieldDialogOpen(false);
              setEditingFieldIndex(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
